import { useState, useEffect, useRef, useCallback } from 'react'
import { supabase } from '@/shared/utils/supabase'

interface UsePhotoLikesRealtimeProps {
  photoId: string
  enabled?: boolean
}

export function usePhotoLikesRealtime({ photoId, enabled = true }: UsePhotoLikesRealtimeProps) {
  const [likeCount, setLikeCount] = useState<number>(0)
  const [hasUserLiked, setHasUserLiked] = useState<boolean>(false)
  const channelRef = useRef<any>(null)

  // Update like count optimistically
  const incrementLikeCount = useCallback(() => {
    setLikeCount(prev => prev + 1)
  }, [])

  const decrementLikeCount = useCallback(() => {
    setLikeCount(prev => Math.max(0, prev - 1))
  }, [])

  useEffect(() => {
    if (!enabled || !photoId) {
      return
    }

    // Subscribe to photo_likes changes for this specific photo
    const channel = supabase
      .channel(`photo_likes_${photoId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'photo_likes',
          filter: `photo_id=eq.${photoId}`
        },
        () => {
          incrementLikeCount()
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'photo_likes',
          filter: `photo_id=eq.${photoId}`
        },
        () => {
          decrementLikeCount()
        }
      )
      .subscribe()

    channelRef.current = channel

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current)
        channelRef.current = null
      }
    }
  }, [photoId, enabled, incrementLikeCount, decrementLikeCount])

  return {
    likeCount,
    hasUserLiked,
    setLikeCount,
    setHasUserLiked,
    isConnected: channelRef.current?.state === 'SUBSCRIBED'
  }
}
